// 契約台帳ビューのリスク判定（§7-3「リスクで炙り出す」／§13-8）。
// 閾値は【暫定・運用で育てる】（§14）。為替換算は Phase2 のため、大額判定は通貨別の暫定値で行う。

import type { Contract } from "./types";

export type RiskKey = "expiring" | "auto_renew" | "special" | "large";
type Tone = "gray" | "amber" | "green" | "red" | "blue";

export interface RiskFlag {
  key: RiskKey;
  label: string; // 繁体字（zh-TW）
  tone: Tone;
}

// 満了接近とみなす日数（暫定）。
export const EXPIRY_WINDOW_DAYS = 90;

// 大額とみなす通貨別の暫定閾値（為替換算は Phase2）。
export const LARGE_AMOUNT_THRESHOLDS: Record<string, number> = {
  NTD: 1_000_000,
  JPY: 5_000_000,
  USD: 30_000,
};

const DAY_MS = 24 * 60 * 60 * 1000;

// "YYYY-MM-DD" などの日付文字列から、今日までの残日数を返す（過去は負、無効/空は null）。
export function daysUntil(dateStr: string | null, today: Date = new Date()): number | null {
  if (!dateStr) return null;
  const target = new Date(dateStr.length === 10 ? `${dateStr}T00:00:00Z` : dateStr);
  if (Number.isNaN(target.getTime())) return null;
  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return Math.ceil((target.getTime() - todayUtc) / DAY_MS);
}

export function isExpiringSoon(c: Contract, today: Date = new Date()): boolean {
  const d = daysUntil(c.period_end, today);
  return d !== null && d >= 0 && d <= EXPIRY_WINDOW_DAYS;
}

export function isAutoRenew(c: Contract): boolean {
  return c.continuity === "auto_renew" || c.special_clauses.includes("auto_renew");
}

export function hasSpecialClauses(c: Contract): boolean {
  return c.special_clauses.length > 0;
}

export function isLargeAmount(c: Contract): boolean {
  if (c.amount == null || !c.currency) return false;
  const thr = LARGE_AMOUNT_THRESHOLDS[c.currency];
  return thr != null && c.amount >= thr;
}

// 契約に付くリスクフラグ一覧（炙り出し用の表示タグ）。
export function computeRiskFlags(c: Contract, today: Date = new Date()): RiskFlag[] {
  const flags: RiskFlag[] = [];
  if (isExpiringSoon(c, today)) flags.push({ key: "expiring", label: "滿期接近", tone: "red" });
  if (isAutoRenew(c)) flags.push({ key: "auto_renew", label: "自動續約", tone: "amber" });
  if (hasSpecialClauses(c)) flags.push({ key: "special", label: "特殊條款", tone: "blue" });
  if (isLargeAmount(c)) flags.push({ key: "large", label: "大額", tone: "red" });
  return flags;
}

// 並べ替え用の緊急度スコア（小さいほど上）。満了接近を最優先。
export function urgencyScore(c: Contract, today: Date = new Date()): number {
  const d = daysUntil(c.period_end, today);
  if (isExpiringSoon(c, today) && d !== null) return d; // 0〜90
  return 100000 - (c.amount ?? 0) / 1e9; // 満了接近以外は金額が大きいものを上に
}
